import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, ApiError } from "../api";
import type { ID } from "../id";
import { liveCommand } from "../liveCommand";
import { STATION_KIND_LABELS } from "@shared/live/labels";

/**
 * Judging panels (poroty) of a competition: which panel judges which
 * block of the running order, and who sits on which seat
 * (docs/zivy-provoz-soutezi.md §11c A.4). Every block names its panel
 * explicitly.
 *
 * Before the competition runs the organizer edits through
 * `/competitions/:id/panels…`; while it runs (Probíhá) the same changes are
 * live commands (`POST /live/:id/commands`, R25). The hooks take `running`
 * and pick the door, so the page does not have to.
 */

export type SeatKind = "plus" | "tech" | "head";

export interface PanelSeat {
  /** Absent until somebody first sits there. */
  id?: ID;
  kind: SeatKind;
  position: number;
  user_id?: ID;
  user_name?: string;
  /** A judge outside the federation, by name only. */
  judge_name?: string;
  /** A seat beyond what the panel's blocks ask for, kept because it is held. */
  extra?: boolean;
}

export interface Panel {
  id: ID;
  name: string;
  /** What the panel's most demanding block asks for. */
  plus_judges: number;
  tech_judges: number;
  seats: PanelSeat[] | null;
  blocks: ID[];
}

export interface PanelBlock {
  item_id: ID;
  competition_category_id?: ID;
  discipline: string;
  /** 1-based lane group and lane, for a block in a lane. */
  lane_group?: number;
  lane?: number;
  plus_judges: number;
  tech_judges: number;
  /** The panel judging the block. */
  panel_id?: ID;
}

/** Who moves the competition on (R11): a device function. */
export type Advancer = "moderator" | "head_judge";

export interface PanelLayout {
  advancer: Advancer;
  /** The tolerance threshold (R12) in centipoints; 0 off. */
  tolerance_cp: number;
  panels: Panel[] | null;
  head_judge: PanelSeat;
  blocks: PanelBlock[] | null;
}

export interface Judge {
  id: ID;
  name: string;
}

const layoutKey = (competitionId: ID) => [
  "competitions",
  "panels",
  competitionId,
];

export function usePanels(competitionId: ID | undefined) {
  return useQuery<PanelLayout>({
    queryKey: layoutKey(competitionId ?? ""),
    enabled: !!competitionId,
    // Several organizers lay panels out at once, and on the day the head
    // judge changes them too: never served from cache (see useStartList).
    staleTime: 0,
    queryFn: () => apiGet<PanelLayout>(`/competitions/${competitionId}/panels`),
  });
}

/**
 * The judges a seat can be given: only those the panel rules would accept
 * there (the server tries each one against the same check a save runs).
 * Without a seat, every user with the Porotce role.
 */
export function useJudges(
  competitionId: ID | undefined,
  seat?: { panel: ID | null; kind: SeatKind; position: number } | null,
) {
  const params = seat
    ? `?kind=${seat.kind}&position=${seat.position}${seat.panel ? `&panel_id=${seat.panel}` : ""}`
    : "";
  return useQuery<Judge[]>({
    queryKey: ["competitions", "judges", competitionId, params],
    enabled: !!competitionId && seat !== null,
    // The answer depends on every other seat, which changes under us.
    staleTime: 0,
    queryFn: () =>
      apiGet<Judge[]>(`/competitions/${competitionId}/judges${params}`),
  });
}

/** Sends one live command as the organizer's session (§7). */

/**
 * A panel mutation. `rendered` says every call site shows the error itself
 * (a dialog), so the global toast must stay out; a mutation fired from an
 * inline select leaves it to the toast.
 */
function usePanelMutation<TVars>(
  competitionId: ID,
  mutationFn: (vars: TVars) => Promise<unknown>,
  rendered: boolean,
) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, TVars>({
    mutationFn,
    meta: rendered ? { suppressErrorToast: true } : undefined,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: layoutKey(competitionId) }),
  });
}

export function useCreatePanel(competitionId: ID, running: boolean) {
  return usePanelMutation<string>(
    competitionId,
    (name) =>
      running
        ? liveCommand(competitionId, "panel.create", { name })
        : apiPost(`/competitions/${competitionId}/panels`, { name }),
    true,
  );
}

export function useRenamePanel(competitionId: ID) {
  return usePanelMutation<{ id: ID; name: string }>(
    competitionId,
    (p) =>
      apiPatch(`/competitions/${competitionId}/panels/${p.id}`, {
        name: p.name,
      }),
    true,
  );
}

export function useDeletePanel(competitionId: ID) {
  return usePanelMutation<ID>(
    competitionId,
    (id) => apiDelete(`/competitions/${competitionId}/panels/${id}`),
    true,
  );
}

/** Gives every block without a panel one: the panel of the block above it,
 * or a new one at the head of the running order and of each lane. */
export function usePreparePanels(competitionId: ID) {
  return usePanelMutation<void>(
    competitionId,
    () => apiPost(`/competitions/${competitionId}/panels/prepare`, {}),
    false,
  );
}

/** Gives one block a panel. */
export function useAssignBlock(competitionId: ID, running: boolean) {
  return usePanelMutation<{ item: ID; panel: ID }>(
    competitionId,
    (p) =>
      running
        ? liveCommand(competitionId, "panel.block_assign", {
            item: p.item,
            panel: p.panel,
          })
        : apiPut(`/competitions/${competitionId}/panels/blocks/${p.item}`, {
            panel_id: p.panel,
          }),
    false,
  );
}

/** Who moves the competition on; on site the command panel.set_advancer. */
export function useSetAdvancer(competitionId: ID, running: boolean) {
  return usePanelMutation<Advancer>(
    competitionId,
    (advancer) =>
      running
        ? liveCommand(competitionId, "panel.set_advancer", { advancer })
        : apiPut(`/competitions/${competitionId}/panels/advancer`, {
            advancer,
          }),
    false,
  );
}

/** The tolerance threshold (R12); on site the command panel.set_tolerance. */
export function useSetTolerance(competitionId: ID, running: boolean) {
  return usePanelMutation<number>(
    competitionId,
    (cp) =>
      running
        ? liveCommand(competitionId, "panel.set_tolerance", { tolerance_cp: cp })
        : apiPut(`/competitions/${competitionId}/panels/tolerance`, {
            tolerance_cp: cp,
          }),
    false,
  );
}

export interface SeatAssignment {
  panel: ID | null;
  kind: SeatKind;
  position: number;
  user: ID | null;
  name: string;
}

export function useAssignSeat(competitionId: ID, running: boolean) {
  return usePanelMutation<SeatAssignment>(
    competitionId,
    (s) =>
      running
        ? liveCommand(competitionId, "panel.seat_assign", s)
        : apiPut(`/competitions/${competitionId}/panels/seats`, {
            panel_id: s.panel,
            kind: s.kind,
            position: s.position,
            user_id: s.user,
            name: s.name,
          }),
    true,
  );
}

/** The judges' seats are named once, in shared/ (the hall app says the same). */
export const SEAT_KIND_LABELS: Record<SeatKind, string> = {
  ...STATION_KIND_LABELS,
  head: "Předseda poroty",
};

/** „Plusový porotce 2", „Předseda poroty". */
export function seatLabel(s: Pick<PanelSeat, "kind" | "position">): string {
  return s.kind === "head"
    ? SEAT_KIND_LABELS.head
    : `${SEAT_KIND_LABELS[s.kind]} ${s.position}`;
}

/** Who sits on a seat, or null when nobody does. */
export function seatHolder(s: PanelSeat): string | null {
  return s.user_name || s.judge_name || null;
}
